import React, { useState, useCallback, useEffect } from 'react';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { toast } from 'react-toastify';
import useJobs from '../hooks/queries/useJobs';
import AdminOpenJob from './AdminOpenJob';
import AdminClosedJob from './AdminClosedJob';
import AdminPanel from './AdminPanel';
import JobDialog from '../JobDialog';
import CloseJobDialog from './CloseJobDialog';
import ConfirmationDialog from '../ConfirmationDialog';
import ApplicantDialog from './ApplicantDialog';
import reopenJob from '../functions/reopenJob';
import { IS_OPEN_TRUE } from '../assets/constants/jobConstants';

const AdminJob = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const [selectedApplicant, setSelectedApplicant] = useState();
  const [closeJobPostingDialogOpen, setCloseJobPostingDialogOpen] =
    useState(false);
  const [jobToClose, setJobToClose] = useState({});
  const [reopenConfirmationOpen, setReopenConfirmationOpen] = useState(false);
  const [jobToReopen, setJobToReopen] = useState({});
  const [applicantDialogOpen, setApplicantDialogOpen] = useState(false);

  const { data: allJobs, isLoading, refetch } = useJobs();

  const metricsRef = React.useRef();
  const tableDataRef = React.useRef();
  const allDataRef = React.useRef();
  const refs = { metricsRef, tableDataRef, allDataRef };

  const onComplete = useCallback(async () => {
    await refetch();
  }, [refetch]);

  useEffect(() => {
    if (selectedRow) {
      setSelectedRow(allJobs.find((job) => job.id === selectedRow.id));
    }
  }, [allJobs, selectedRow]);

  const onApplicantDialogClose = useCallback(() => {
    setSelectedApplicant();
    setApplicantDialogOpen(false);
  }, []);

  const onApplicantDialogOpen = useCallback((rowData = {}) => {
    if (rowData.name) {
      setSelectedApplicant(rowData);
    }
    setApplicantDialogOpen(true);
  }, []);

  const onReopenConfirmationClose = useCallback(() => {
    setJobToReopen({});
    setReopenConfirmationOpen(false);
  }, []);

  const selectedJobToReopen = useCallback((job) => {
    setJobToReopen(job);
    setReopenConfirmationOpen(true);
  }, []);

  const confirmedReopen = useCallback(
    async (job) => {
      try {
        await reopenJob(job);
        toast.success('Reopened job posting successfully.');
      } catch {
        toast.error('Failed to reopen job posting.');
      } finally {
        onReopenConfirmationClose();
        onComplete();
      }
    },
    [onComplete, onReopenConfirmationClose]
  );

  const handleTabChange = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const onClose = useCallback(() => {
    setSelectedRow();
    setOpen(false);
  }, []);

  const onOpen = useCallback((rowData = {}) => {
    if (rowData.jobId) {
      setSelectedRow(rowData);
    }
    setOpen(true);
  }, []);

  const onCloseJobPostingDialogClose = useCallback(() => {
    setJobToClose({});
    setCloseJobPostingDialogOpen(false);
  }, []);

  const selectedJobToClose = useCallback((job) => {
    setJobToClose(job);
    setCloseJobPostingDialogOpen(true);
  }, []);

  return (
    <>
      <AdminPanel onOpen={onOpen} refs={refs} />
      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        centered
      >
        <Tab label="OPEN JOBS" />
        <Tab label="CLOSED JOBS" />
      </Tabs>
      {currentTab === 0 ? (
        <>
          <AdminOpenJob
            jobs={allJobs}
            isLoading={isLoading}
            onOpen={onOpen}
            onComplete={onComplete}
            selectedJobToClose={selectedJobToClose}
            refs={refs}
          />
        </>
      ) : (
        <>
          <AdminClosedJob
            jobs={allJobs}
            isLoading={isLoading}
            onOpen={onOpen}
            selectedJobToReopen={selectedJobToReopen}
            refs={refs}
          />
        </>
      )}

      <JobDialog
        onClose={onClose}
        onComplete={onComplete}
        open={open}
        selectedJobData={selectedRow}
        selectedJobToClose={selectedJobToClose}
        selectedJobToReopen={selectedJobToReopen}
        onApplicantDialogOpen={onApplicantDialogOpen}
      />
      {jobToClose.jobId ? (
        <CloseJobDialog
          onComplete={onComplete}
          onClose={onCloseJobPostingDialogClose}
          open={closeJobPostingDialogOpen}
          job={jobToClose}
        />
      ) : null}
      {jobToReopen.jobId ? (
        <ConfirmationDialog
          onClose={onReopenConfirmationClose}
          open={reopenConfirmationOpen}
          job={jobToReopen}
          confirmedAction={confirmedReopen}
          confirmationAction="Reopen"
          confirmationText={
            <>
              Please confirm you want to reopen the job posting{' '}
              <b>
                {jobToReopen.jobId}: {jobToReopen.jobTitle}
              </b>
            </>
          }
          confirmationTitle={<b>Reopen Confirmation ({jobToReopen.jobId})</b>}
        />
      ) : null}
      {applicantDialogOpen ? (
        <ApplicantDialog
          open={applicantDialogOpen}
          onComplete={onComplete}
          onClose={onApplicantDialogClose}
          job={selectedRow}
          selectedApplicant={selectedApplicant}
          disabled={selectedRow.isOpen !== IS_OPEN_TRUE}
        />
      ) : null}
    </>
  );
};

export default AdminJob;
